import { isUrl } from '@/utility/index';
import { useBrand } from '@/lib/brand/BrandContext';
import Head from 'next/head';
import { useRouter } from 'next/router';

interface Meta {
  title?: string;
  description?: string;
  /** relative path */
  image?: string;
  type?: string;
  date?: string;
}

const withTrailingSlash = (origin: string) => `${origin.replace(/\/$/, '')}/`;

const CustomHead = ({ ...customMeta }: Meta) => {
  const router = useRouter();
  const brand = useBrand();

  const requestBaseUrl = withTrailingSlash(
    brand.requestOrigin ?? brand.publicOrigin
  );
  const canonicalBaseUrl = withTrailingSlash(
    brand.canonicalOrigin ?? brand.publicOrigin
  );
  const currentUrl = new URL(router.asPath, requestBaseUrl).toString();
  const canonicalUrl = new URL(router.asPath, canonicalBaseUrl).toString();

  const defaultMeta = {
    title: brand.appName,
    description: 'Collaborative learning platform for students and teachers',
    image: brand.logoSrc,
    type: 'website',
  };

  // returns image with absolute path
  const handleCustomMeta = (_meta: Meta): Meta => {
    const { image } = _meta;
    if (!image || isUrl(image)) return _meta;
    return { ..._meta, image: new URL(image, requestBaseUrl).toString() };
  };

  const meta = { ...defaultMeta, ...handleCustomMeta(customMeta) };

  return (
    <Head>
      <title>{meta.title}</title>
      <meta charSet="utf-8" />
      {/* should handle position: fixed on Chrome mobile, it doesn't work */}
      <meta
        name="viewport"
        content="initial-scale=1.0, minimum-scale=1.0, height=device-height, width=device-width"
      />

      <meta name="robots" content="follow, index" />
      <meta content={meta.description} name="description" />
      <link rel="canonical" href={canonicalUrl} />

      {/* og */}
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={meta.type} />
      <meta property="og:site_name" content={brand.appName} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:image" content={meta.image} />

      {/* tw */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.image} />

      {meta.date && (
        <meta property="article:published_time" content={meta.date} />
      )}
    </Head>
  );
};

export default CustomHead;
